<template>
  <th
    class="sb-data-table__head-cell"
    :class="computedClasses"
    :aria-sort="ariaSort"
    scope="col"
  >
    <template v-if="isSortable && !hasHeaderSlot">
      <span :id="`datatable-sort-message-${index}`" class="screen-reader-only">
        {{ sortMessage }}
      </span>
      <button
        class="sb-data-table__head-sort-button"
        data-testid="datatable-sort-button"
        :aria-describedby="`datatable-sort-message-${index}`"
        @click="toggleSort"
      >
        <span class="sb-data-table__head-sort-button-label">
          {{ column.label }}
        </span>
        <SbIcon
          class="sb-data-table__sort-icon"
          :name="$options.iconsSort[order + 1]"
        />
      </button>
    </template>
    <div v-else-if="hasHeaderSlot" class="sb-data-table__header-template">
      <VNodes :vnodes="column.scopedSlots.header()" />
    </div>
    <span v-else-if="column.hideLabel" class="screen-reader-only">
      {{ column.label }}
    </span>
    <template v-else> {{ column.label }} </template>
  </th>
</template>

<script lang="ts">
import SbIcon from '../../Icon'
import VNodes from '../../../utils/VNodes'

export default {
  name: 'SbDataTableHeaderCell',

  components: {
    SbIcon,
    VNodes,
  },

  iconsSort: ['arrow-down', 'arrow-up-down', 'arrow-up'],

  inject: ['dataTableContext'],

  props: {
    column: {
      type: Object,
      default: null,
    },
    sortedKey: {
      type: String,
      default: null,
    },
    isSortIconAlwaysVisible: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
    order: 0,
  }),

  computed: {
    context() {
      return this.dataTableContext()
    },

    isSortable() {
      return this.column.sortable
    },

    isSortedKey() {
      return this.sortKey === this.sortedKey
    },

    showSortIcon() {
      return (
        (this.isSortable && this.order && this.isSortedKey) ||
        (this.isSortable && this.isSortIconAlwaysVisible)
      )
    },

    sortKey() {
      return this.column.value
    },

    hasHeaderSlot() {
      return this.column?.scopedSlots?.header
    },

    computedClasses() {
      return {
        'sb-data-table--show-icon': this.showSortIcon,
        'sb-data-table__head-cell--centered': this.column?.isContentCentered,
        'sb-data-table__head-cell--sortable': this.column?.sortable,
      }
    },

    ariaSort() {
      if (this.isSortedKey) {
        return this.order === 1 ? 'ascending' : 'descending'
      }
      return 'none'
    },

    sortMessage() {
      if (this.order === -1) {
        return `Click to unsort rows by ${this.column.label} header`
      }

      return `Click to sort rows by ${this.column.label} header in ${
        this.order === 1 ? 'descending' : 'ascending'
      } order`
    },
  },

  watch: {
    sortedKey() {
      if (!this.isSortedKey) this.order = 0
    },
  },

  methods: {
    toggleSort() {
      if (this.sortKey && this.isSortable) {
        this.order = this.order === 0 || this.order === -1 ? this.order + 1 : -1

        if (this.order === 0) {
          this.context.resetTableOrder()
          return
        }

        this.context.toggleTableOrder(this.order, this.sortKey)
      } else if (!this.sortKey && this.isSortable) {
        throw new Error(
          'Must provide the value property when sortable is defined.',
        )
      }
    },
  },
}
</script>
